import { gunzipSync } from 'fflate';

/**
 * CapTableCacheService
 *
 * In-memory cache for cap table data with Web Worker-based LZString
 * compression/decompression to keep heavy work off the main thread.
 *
 * Usage:
 *   CapTableCacheService.init()                     — create worker (call once at startup)
 *   CapTableCacheService.get(client)                — sync read from cache (null if not loaded)
 *   CapTableCacheService.set(client, data, compress) — cache + async compress to sessionStorage
 *   CapTableCacheService.loadFromSessionStorage(client) — async decompress from sessionStorage
 *   CapTableCacheService.clear(client)              — remove from cache
 */

// LZString source embedded for the inline Web Worker (no external file needed).
// This is the same IIFE from src/shared_assets/lzstring.js (~4KB minified).
const LZSTRING_SOURCE = `
let LZString=(function(){let r=String.fromCharCode,o="ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=",n="ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+-$",e={};function t(r,o){if(!e[r]){e[r]={};for(let n=0;n<r.length;n++)e[r][r.charAt(n)]=n}return e[r][o]}const i={compressToBase64:function(r){if(null==r)return"";let n=i._compress(r,6,function(r){return o.charAt(r)});switch(n.length%4){default:case 0:return n;case 1:return n+"===";case 2:return n+"==";case 3:return n+"="}},decompressFromBase64:function(r){return null==r?"":(""==r?null:i._decompress(r.length,32,function(n){return t(o,r.charAt(n))}))},compressToUTF16:function(o){return null==o?"":i._compress(o,15,function(o){return r(o+32)})+" "},decompressFromUTF16:function(r){return null==r?"":(""==r?null:i._decompress(r.length,16384,function(o){return r.charCodeAt(o)-32}))},compressToUint8Array:function(r){const o=i.compress(r),n=new Uint8Array(2*o.length),t=o.length;for(let e=0;e<t;e++){let s=o.charCodeAt(e);n[2*e]=s>>>8;n[2*e+1]=s%256}return n},decompressFromUint8Array:function(o){if(null==o)return i.decompress(o);const n=new Array(o.length/2),t=n.length;for(let e=0;e<t;e++)n[e]=256*o[2*e]+o[2*e+1];let s=[];return n.forEach(function(o){s.push(r(o))}),i.decompress(s.join(""))},compressToEncodedURIComponent:function(r){return null==r?"":i._compress(r,6,function(r){return n.charAt(r)})},decompressFromEncodedURIComponent:function(r){return null==r?"":(""==r?null:(r=r.replace(/ /g,"+"),i._decompress(r.length,32,function(o){return t(n,r.charAt(o))})))},compress:function(o){return i._compress(o,16,function(o){return r(o)})},_compress:function(r,o,n){if(null==r)return"";let e,t,i,s={},u={},a="",p="",c="",l=2,f=3,h=2,d=[],m=0,v=0;for(i=0;i<r.length;i+=1)if(a=r.charAt(i),Object.prototype.hasOwnProperty.call(s,a)||(s[a]=f++,u[a]=!0),p=c+a,Object.prototype.hasOwnProperty.call(s,p))c=p;else{if(Object.prototype.hasOwnProperty.call(u,c)){if(c.charCodeAt(0)<256){for(e=0;e<h;e++)m<<=1,v==o-1?(v=0,d.push(n(m)),m=0):v++;for(t=c.charCodeAt(0),e=0;e<8;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1}else{for(t=1,e=0;e<h;e++)m=(m<<1)|t,v==o-1?(v=0,d.push(n(m)),m=0):v++,t=0;for(t=c.charCodeAt(0),e=0;e<16;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1}0==--l&&(l=Math.pow(2,h),h++),delete u[c]}else for(t=s[c],e=0;e<h;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1;0==--l&&(l=Math.pow(2,h),h++),s[p]=f++,c=String(a)}if(""!==c){if(Object.prototype.hasOwnProperty.call(u,c)){if(c.charCodeAt(0)<256){for(e=0;e<h;e++)m<<=1,v==o-1?(v=0,d.push(n(m)),m=0):v++;for(t=c.charCodeAt(0),e=0;e<8;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1}else{for(t=1,e=0;e<h;e++)m=(m<<1)|t,v==o-1?(v=0,d.push(n(m)),m=0):v++,t=0;for(t=c.charCodeAt(0),e=0;e<16;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1}0==--l&&(l=Math.pow(2,h),h++),delete u[c]}else for(t=s[c],e=0;e<h;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1;0==--l&&(l=Math.pow(2,h),h++)}for(t=2,e=0;e<h;e++)m=(m<<1)|(1&t),v==o-1?(v=0,d.push(n(m)),m=0):v++,t>>=1;for(;;){if(m<<=1,v==o-1){d.push(n(m));break}v++}return d.join("")},decompress:function(r){return null==r?"":(""==r?null:i._decompress(r.length,32768,function(o){return r.charCodeAt(o)}))},_decompress:function(o,n,e){let t,i,s,u,a,p,c,l=[],f=4,h=4,d=3,m="",v=[],g={val:e(0),position:n,index:1};for(t=0;t<3;t+=1)l[t]=t;for(s=0,a=Math.pow(2,2),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;switch(s){case 0:for(s=0,a=Math.pow(2,8),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;c=r(s);break;case 1:for(s=0,a=Math.pow(2,16),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;c=r(s);break;case 2:return""}for(l[3]=c,i=c,v.push(c);;){if(g.index>o)return"";for(s=0,a=Math.pow(2,d),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;switch(c=s){case 0:for(s=0,a=Math.pow(2,8),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;l[h++]=r(s),c=h-1,f--;break;case 1:for(s=0,a=Math.pow(2,16),p=1;p!=a;)u=g.val&g.position,g.position>>=1,0==g.position&&(g.position=n,g.val=e(g.index++)),s|=(u>0?1:0)*p,p<<=1;l[h++]=r(s),c=h-1,f--;break;case 2:return v.join("")}if(0==f&&(f=Math.pow(2,d),d++),l[c])m=l[c];else{if(c!==h)return null;m=i+i.charAt(0)}v.push(m),l[h++]=i+m.charAt(0),i=m,0==--f&&(f=Math.pow(2,d),d++)}}}; return i})();
`;

// Saved-copy format: GZ_PREFIX + byte count + ':' + gzip bytes packed 15 bits per character
// (offset by 32, so no control or surrogate characters). Copies without the prefix are LZString.
const GZ_PREFIX = '\u0001nthgz1:';

const WORKER_CODE = `
${LZSTRING_SOURCE}
const GZ_PREFIX = ${JSON.stringify(GZ_PREFIX)};
function pack(u) {
	const out = new Array(Math.ceil((u.length * 8) / 15));
	let acc = 0, nb = 0, k = 0;
	for (let i = 0; i < u.length; i++) {
		acc = (acc << 8) | u[i];
		nb += 8;
		while (nb >= 15) { nb -= 15; out[k++] = String.fromCharCode(((acc >>> nb) & 0x7fff) + 32); }
		acc &= (1 << nb) - 1;
	}
	if (nb > 0) out[k++] = String.fromCharCode(((acc << (15 - nb)) & 0x7fff) + 32);
	return GZ_PREFIX + u.length + ':' + out.join('');
}
async function gzip(s) {
	const buf = await new Response(new Blob([s]).stream().pipeThrough(new CompressionStream('gzip'))).arrayBuffer();
	return pack(new Uint8Array(buf));
}
self.onmessage = async function(e) {
	const { type, key, data, id } = e.data;
	let result;
	if (type === 'compress') {
		// Native gzip is ~10x faster than LZString on large cap tables; LZString if unavailable
		try { result = typeof CompressionStream !== 'undefined' ? await gzip(data) : LZString.compress(data); }
		catch (err) { result = LZString.compress(data); }
	} else if (type === 'decompress') {
		result = LZString.decompress(data);
	}
	self.postMessage({ type: type + 'd', key: key, data: result, id: id });
};
`;

// Unpack a GZ_PREFIX copy back to JSON text (synchronous, so readCapTable callers keep working)
function decodeGz(raw) {
	const body = raw.slice(GZ_PREFIX.length);
	const c = body.indexOf(':');
	const n = +body.slice(0, c);
	const u = new Uint8Array(n);
	let acc = 0,
		nb = 0,
		k = 0;
	for (let i = c + 1; i < body.length && k < n; i++) {
		acc = (acc << 15) | (body.charCodeAt(i) - 32);
		nb += 15;
		while (nb >= 8 && k < n) {
			nb -= 8;
			u[k++] = (acc >>> nb) & 0xff;
		}
		acc &= (1 << nb) - 1;
	}
	return new TextDecoder().decode(gunzipSync(u));
}

const CapTableCacheService = {
	_cache: {},

	// JSON text of the saved copy for this client ('' if none), whichever format it was saved in
	readSaved(client) {
		const raw = sessionStorage.getItem(client + '.CapTable');
		if (!raw) return '';
		if (raw.startsWith(GZ_PREFIX)) return decodeGz(raw);
		const lz = LZString.decompress(raw);
		return lz != null && lz !== '' ? lz : raw;
	},
	_worker: null,
	_pendingCallbacks: {},
	_nextId: 0,

	init() {
		try {
			const blob = new Blob([WORKER_CODE], { type: 'application/javascript' });
			this._worker = new Worker(URL.createObjectURL(blob));
			this._worker.onmessage = (e) => {
				const { id, data } = e.data;
				const cb = this._pendingCallbacks[id];
				if (cb) {
					delete this._pendingCallbacks[id];
					cb(data);
				}
			};
			this._worker.onerror = (err) => {
				console.error('[CapTableCache] Worker error:', err);
			};
			console.log('[Performance] LZString Web Worker initialized');
		} catch (err) {
			console.warn('[CapTableCache] Worker creation failed, using synchronous fallback:', err);
			this._worker = null;
		}
	},

	/**
	 * Synchronous read from in-memory cache.
	 * Returns the cached array or null if not yet loaded.
	 */
	get(client) {
		return this._cache[client] || null;
	},

	/**
	 * Set in-memory cache and compress to sessionStorage off-thread.
	 * @param {string} client
	 * @param {Array} data - parsed cap table array
	 * @param {boolean} compress - whether this client needs compression
	 */
	// Fast fingerprint of the saved JSON (FNV-1a over the string), so identical re-saves can be skipped
	_signature(json) {
		let h = 0x811c9dc5;
		for (let i = 0; i < json.length; i++) {
			h ^= json.charCodeAt(i);
			h = Math.imul(h, 0x01000193);
		}
		return json.length + ':' + (h >>> 0).toString(16);
	},

	// Record the fingerprint of the copy just read from sessionStorage, so re-saving the same data is a no-op
	noteSaved(client, json) {
		try {
			if (typeof json === 'string' && json) sessionStorage.setItem(client + '.CapTableSig', this._signature(json));
		} catch (e) {}
	},

	set(client, data, compress) {
		this._cache[client] = data;
		const json = JSON.stringify(data);
		const sigKey = client + '.CapTableSig';

		if (compress && this._worker) {
			// Same content already saved: nothing to write (pages re-save the cap table often, and a
			// background write cut short by leaving the page would otherwise cost the next page its copy)
			const sig = this._signature(json);
			try {
				if (sessionStorage.getItem(sigKey) === sig && sessionStorage.getItem(client + '.CapTable')) return;
				// Real change: drop the old copy now, so a navigation before the worker finishes makes
				// the next page rebuild instead of showing stale data (e.g. a holder who was just removed)
				sessionStorage.removeItem(client + '.CapTable');
				sessionStorage.removeItem(sigKey);
			} catch (e) {}
			const id = this._nextId++;
			this._worker.postMessage({ type: 'compress', key: client + '.CapTable', data: json, id: id });
			this._pendingCallbacks[id] = (compressed) => {
				try {
					sessionStorage.setItem(client + '.CapTable', compressed);
					sessionStorage.setItem(sigKey, sig);
				} catch (e) {
					console.error('[CapTableCache] sessionStorage write failed:', e);
					if (!this._alertedWriteFailure) {
						this._alertedWriteFailure = true;
						window.reportAlert?.(
							'portal.size',
							'Cap table not saved: session storage full (' + client + ')',
							'Saved copy ' + Math.round(compressed.length / 1024) + 'K chars. ' + String(e) + '. Pages will rebuild instead of reusing it.'
						);
					}
				}
			};
		} else if (compress) {
			// Synchronous fallback if worker unavailable
			sessionStorage.setItem(client + '.CapTable', LZString.compress(json));
		} else {
			sessionStorage.setItem(client + '.CapTable', json);
		}
	},

	/**
	 * Async load from sessionStorage with worker decompression.
	 * Used on cold page load when cache is empty but sessionStorage has data.
	 * @param {string} client
	 * @returns {Promise<Array>}
	 */
	loadFromSessionStorage(client) {
		return new Promise((resolve) => {
			const raw = sessionStorage.getItem(client + '.CapTable');
			if (!raw) {
				resolve([]);
				return;
			}
			if (raw.startsWith(GZ_PREFIX)) {
				try {
					const json = decodeGz(raw);
					const parsed = JSON.parse(json);
					if (Array.isArray(parsed)) {
						this._cache[client] = parsed;
						this.noteSaved(client, json);
						resolve(parsed);
						return;
					}
				} catch (e) {
					console.error('[CapTableCache] Saved copy unreadable for client ' + client + ':', e);
				}
				resolve([]);
				return;
			}

			if (this._worker) {
				const id = this._nextId++;
				this._worker.postMessage({ type: 'decompress', key: client + '.CapTable', data: raw, id: id });
				this._pendingCallbacks[id] = (decompressed) => {
					try {
						const json = decompressed || raw;
						const parsed = JSON.parse(json);
						if (Array.isArray(parsed)) {
							this._cache[client] = parsed;
							this.noteSaved(client, json);
							resolve(parsed);
						} else {
							console.error('[CapTableCache] Data is not an array for client ' + client);
							resolve([]);
						}
					} catch (e) {
						// Decompression returned garbage — try raw (uncompressed data)
						try {
							const parsed = JSON.parse(raw);
							if (Array.isArray(parsed)) {
								this._cache[client] = parsed;
								resolve(parsed);
							} else {
								resolve([]);
							}
						} catch (e2) {
							console.error('[CapTableCache] JSON parse failed for client ' + client + ':', e2);
							resolve([]);
						}
					}
				};
			} else {
				// Synchronous fallback
				try {
					const decompressed = LZString.decompress(raw);
					const json = decompressed || raw;
					const parsed = JSON.parse(json);
					if (Array.isArray(parsed)) {
						this._cache[client] = parsed;
						resolve(parsed);
					} else {
						resolve([]);
					}
				} catch (e) {
					try {
						const parsed = JSON.parse(raw);
						if (Array.isArray(parsed)) {
							this._cache[client] = parsed;
							resolve(parsed);
						} else {
							resolve([]);
						}
					} catch (e2) {
						console.error('[CapTableCache] Fallback parse failed for client ' + client + ':', e2);
						resolve([]);
					}
				}
			}
		});
	},

	clear(client) {
		delete this._cache[client];
	},
};

export { CapTableCacheService };
